import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Card, Row, Col, Statistic, Progress, Tag, Spin } from 'antd';
import {
  ArrowDownOutlined, BankOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { Expenses } from './Expenses';
import { BudgetPage } from './Budget';
import { ReserveFund } from './ReserveFund';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { apiService } from '../services/api';
import type { Expense, ReserveFund as ReserveFundData, Budget, BudgetLineItem } from '../types';

// ─── KPI strip helpers ─────────────────────────────────────────────────────────
interface KpiData {
  ytdExpenses: number;
  annualBudget: number;
  reserveBalance: number;
  reserveTarget: number;
  loaded: boolean;
}

function currencyFormat(n: number): string {
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const KpiStrip: React.FC<{ condoId: string | undefined }> = ({ condoId }) => {
  const { expenses } = useCondoStore() as any;
  const [kpi, setKpi] = useState<KpiData>({
    ytdExpenses: 0,
    annualBudget: 0,
    reserveBalance: 0,
    reserveTarget: 0,
    loaded: false,
  });

  // YTD expenses derived from Zustand store (already fetched)
  const ytdExpenses: number = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return (expenses as Expense[])
      .filter(e => !condoId || e.condoId === condoId)
      .filter(e => {
        const d = e.date instanceof Date ? e.date : new Date(e.date);
        return d.getFullYear() === currentYear;
      })
      .reduce((sum, e) => sum + (e.amount ?? 0), 0);
  }, [expenses, condoId]);

  // Budget + reserve fund fetched once at hub level for KPIs
  useEffect(() => {
    if (!condoId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [funds, budgets] = await Promise.all([
          apiService.getReserveFunds(condoId),
          apiService.getBudgets(condoId),
        ]);

        const activeFunds = (funds as ReserveFundData[]).filter(f => f.isActive);
        const reserveBalance = activeFunds.reduce((s, f) => s + (f.currentBalance ?? 0), 0);
        const reserveTarget = activeFunds.reduce((s, f) => s + (f.targetBalance ?? 0), 0);

        // Find the ACTIVE budget for the current fiscal year
        const currentYear = new Date().getFullYear();
        const activeBudget = (budgets as Budget[]).find(
          b => b.status === 'ACTIVE' && b.fiscalYear === currentYear,
        ) ?? (budgets as Budget[]).find(b => b.status === 'ACTIVE');

        let annualBudget = 0;
        if (activeBudget?.id) {
          const lineItems = await apiService.getBudgetLineItems(activeBudget.id);
          annualBudget = (lineItems as BudgetLineItem[]).reduce(
            (s, li) => s + (li.budgetedAmount ?? 0),
            0,
          );
        }

        if (!cancelled) {
          setKpi({ ytdExpenses, annualBudget, reserveBalance, reserveTarget, loaded: true });
        }
      } catch {
        if (!cancelled) setKpi(prev => ({ ...prev, loaded: true }));
      }
    };

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoId]);

  // Keep ytdExpenses in sync when store changes
  useEffect(() => {
    setKpi(prev => ({ ...prev, ytdExpenses }));
  }, [ytdExpenses]);

  const budgetUsedPct = kpi.annualBudget > 0
    ? Math.min(100, Math.round((kpi.ytdExpenses / kpi.annualBudget) * 100))
    : 0;
  const reservePct = kpi.reserveTarget > 0
    ? Math.min(100, Math.round((kpi.reserveBalance / kpi.reserveTarget) * 100))
    : 0;
  const variance = kpi.annualBudget - kpi.ytdExpenses;
  const underBudget = variance >= 0;

  if (!kpi.loaded && !expenses.length) {
    return (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <Card
      size="small"
      bordered={false}
      style={{
        marginBottom: 16,
        background: 'linear-gradient(135deg, #f0f5ff 0%, #f6ffed 100%)',
        borderRadius: 10,
        border: '1px solid #d9e8ff',
      }}
      bodyStyle={{ padding: '12px 20px' }}
    >
      <Row gutter={[32, 8]} align="middle">
        {/* YTD Expenses vs Budget */}
        <Col xs={24} sm={10}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <ArrowDownOutlined style={{ color: '#f5222d', fontSize: 16 }} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>YTD Expenses</span>
            {kpi.annualBudget > 0 && (
              <Tag
                color={underBudget ? 'success' : 'error'}
                style={{ marginLeft: 'auto', fontWeight: 600 }}
              >
                {underBudget ? 'Under' : 'Over'} Budget&nbsp;
                {currencyFormat(Math.abs(variance))}
              </Tag>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#f5222d' }}>
              {currencyFormat(kpi.ytdExpenses)}
            </span>
            {kpi.annualBudget > 0 && (
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                of {currencyFormat(kpi.annualBudget)} budget
              </span>
            )}
          </div>
          {kpi.annualBudget > 0 && (
            <Progress
              percent={budgetUsedPct}
              strokeColor={budgetUsedPct > 90 ? '#f5222d' : budgetUsedPct > 75 ? '#fa8c16' : '#52c41a'}
              trailColor="#fff"
              size="small"
              format={p => `${p}%`}
            />
          )}
        </Col>

        {/* Reserve Fund */}
        <Col xs={24} sm={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <BankOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>Reserve Fund</span>
            {kpi.reserveTarget > 0 && (
              <Tag color={reservePct >= 80 ? 'success' : reservePct >= 50 ? 'warning' : 'error'}
                style={{ marginLeft: 'auto', fontWeight: 600 }}>
                {reservePct}% Funded
              </Tag>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#1890ff' }}>
              {currencyFormat(kpi.reserveBalance)}
            </span>
            {kpi.reserveTarget > 0 && (
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                of {currencyFormat(kpi.reserveTarget)} target
              </span>
            )}
          </div>
          {kpi.reserveTarget > 0 && (
            <Progress
              percent={reservePct}
              strokeColor={reservePct >= 80 ? '#52c41a' : reservePct >= 50 ? '#fa8c16' : '#f5222d'}
              trailColor="#fff"
              size="small"
              format={p => `${p}%`}
            />
          )}
        </Col>

        {/* Budget Variance summary (if budget loaded) */}
        {kpi.annualBudget > 0 && (
          <Col xs={24} sm={6}>
            <Statistic
              title={<span style={{ fontSize: 12, fontWeight: 600 }}>Budget Variance (YTD)</span>}
              value={Math.abs(variance)}
              prefix={underBudget ? '+' : '-'}
              formatter={v => currencyFormat(Number(v))}
              valueStyle={{ color: underBudget ? '#52c41a' : '#f5222d', fontSize: 20 }}
              suffix={
                <BarChartOutlined
                  style={{ fontSize: 14, color: underBudget ? '#52c41a' : '#f5222d', marginLeft: 4 }}
                />
              }
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

// ─── Main Hub ──────────────────────────────────────────────────────────────────
/**
 * S14 — Financial Management Hub
 * Consolidates Expenses, Budget, and Reserve Fund into one page.
 * A persistent KPI strip above the tabs shows YTD spend, reserve health,
 * and budget variance at a glance regardless of which tab is active.
 */
const FinancialManagement: React.FC = () => {
  const { currentCondo } = useAuthStore();
  const condoId = currentCondo?.id;

  const items = [
    {
      key: 'expenses',
      label: (
        <span>
          <ArrowDownOutlined />
          &nbsp;Expenses
        </span>
      ),
      children: <Expenses />,
    },
    {
      key: 'budget',
      label: (
        <span>
          <BarChartOutlined />
          &nbsp;Budget
        </span>
      ),
      children: <BudgetPage />,
    },
    {
      key: 'reserve-fund',
      label: (
        <span>
          <BankOutlined />
          &nbsp;Reserve Fund
        </span>
      ),
      children: <ReserveFund />,
    },
  ];

  return (
    <div>
      <KpiStrip condoId={condoId} />
      <Tabs
        items={items}
        size="large"
        destroyInactiveTabPane={false}
        style={{ marginTop: -4 }}
      />
    </div>
  );
};

export default FinancialManagement;
